import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TatuadorFormProps {
  initialData?: any;
  onSubmit: (data: {
    nome: string;
    email: string | null;
    telefone: string | null;
    instagram: string | null;
    observacoes: string | null;
    comissao_percentual: number;
    permissao_clientes: "todos" | "proprios";
    permissao_estoque: "todos" | "nenhum";
  }) => void;
  onCancel: () => void;
  title: string;
  submitLabel?: string;
  /** true quando o tatuador já aceitou o convite (não dá para trocar e-mail) */
  jaVinculado?: boolean;
}

export function TatuadorForm({ initialData, onSubmit, onCancel, title, submitLabel, jaVinculado }: TatuadorFormProps) {
  const [f, setF] = useState({
    nome: initialData?.nome || "",
    email: initialData?.email || "",
    telefone: initialData?.telefone || "",
    instagram: initialData?.instagram || "",
    comissao:
      initialData?.comissao_percentual?.toString() ??
      initialData?.comissao?.toString() ??
      "30",
    observacoes: initialData?.observacoes || "",
    permissaoClientes: initialData?.permissao_clientes || "proprios",
    permissaoEstoque: initialData?.permissao_estoque || "nenhum",
  });
  const [erro, setErro] = useState<string | null>(null);
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    if (!f.nome.trim()) {
      setErro("Informe o nome do tatuador");
      return;
    }
    const comissao = parseFloat(f.comissao.replace(",", "."));
    if (isNaN(comissao) || comissao < 0 || comissao > 100) {
      setErro("A comissão deve ser um número entre 0 e 100");
      return;
    }
    onSubmit({
      nome: f.nome.trim(),
      email: f.email.trim() || null,
      telefone: f.telefone.trim() || null,
      instagram: f.instagram.trim() || null,
      observacoes: f.observacoes.trim() || null,
      comissao_percentual: comissao,
      permissao_clientes: f.permissaoClientes,
      permissao_estoque: f.permissaoEstoque,
    });
  };

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-lg mx-auto shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{title}</h2>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-white/5">
          <X size={24} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm font-medium">Nome completo *</Label>
          <Input
            value={f.nome}
            onChange={(e) => set("nome", e.target.value)}
            placeholder="Ex: João da Silva"
            className="bg-background border-border focus-visible:border-primary h-11"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">E-mail</Label>
            <Input
              type="email"
              value={f.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="tatuador@email.com"
              disabled={jaVinculado}
              className="bg-background border-border focus-visible:border-primary h-11 disabled:opacity-60"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Telefone</Label>
            <Input
              value={f.telefone}
              onChange={(e) => set("telefone", e.target.value)}
              placeholder="(00) 00000-0000"
              className="bg-background border-border focus-visible:border-primary h-11"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Instagram</Label>
            <Input
              value={f.instagram}
              onChange={(e) => set("instagram", e.target.value)}
              placeholder="@usuario"
              className="bg-background border-border focus-visible:border-primary h-11"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Comissão do studio (%) *</Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={f.comissao}
                onChange={(e) => set("comissao", e.target.value)}
                className="bg-background border-border focus-visible:border-primary h-11 pr-8"
                required
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground -mt-2">
          Percentual do valor de cada tatuagem do tatuador que é repassado ao studio.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Acesso a Clientes</Label>
            <Select value={f.permissaoClientes} onValueChange={(v) => set("permissaoClientes", v)}>
              <SelectTrigger className="bg-background border-border h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                <SelectItem value="proprios">Só os clientes que ele atende</SelectItem>
                <SelectItem value="todos">Todos os clientes do estúdio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Acesso ao Estoque</Label>
            <Select value={f.permissaoEstoque} onValueChange={(v) => set("permissaoEstoque", v)}>
              <SelectTrigger className="bg-background border-border h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                <SelectItem value="nenhum">Sem acesso</SelectItem>
                <SelectItem value="todos">Vê o estoque do estúdio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm font-medium">Observações</Label>
          <Textarea
            value={f.observacoes}
            onChange={(e) => set("observacoes", e.target.value)}
            placeholder="Especialidade, dias que trabalha, acordos, etc."
            className="bg-background border-border focus-visible:border-primary min-h-[90px] resize-none"
          />
        </div>

        {erro && (
          <p className="text-sm text-primary bg-primary/10 border border-primary/20 rounded-lg p-3">{erro}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="submit" className="flex-1 bg-primary hover:bg-primary/90 text-white font-semibold h-12">
            {submitLabel || "Salvar e gerar convite"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            className="px-6 border-border bg-transparent text-muted-foreground hover:bg-white/5 hover:text-foreground h-12"
          >
            Cancelar
          </Button>
        </div>
      </form>
    </div>
  );
}
